import { useLayoutEffect, useState } from "react";

export const POPOVER_GAP = 8;
export const VIEWPORT_MARGIN = 12;
const MIN_POPOVER_HEIGHT = 96;

/** "bottom" unless the content does not fit below and there is more room above. */
export function choosePopoverPlacement({ spaceAbove, spaceBelow, desiredHeight, gap = POPOVER_GAP }) {
  return spaceBelow < desiredHeight + gap && spaceAbove > spaceBelow ? "top" : "bottom";
}

/**
 * Viewport-fixed placement for a popover anchored to a trigger rect.
 * Bottom placement sets ``top``; top placement sets ``bottom`` so the panel
 * stays attached to the trigger whatever its rendered height. ``maxHeight``
 * is the usable space on the chosen side (never more than the configured
 * cap), and the panel is shifted horizontally to stay inside the viewport.
 */
export function computePopoverPosition({
  anchor,
  viewport,
  contentHeight,
  contentWidth,
  maxHeight = Infinity,
  align = "start",
  gap = POPOVER_GAP,
  margin = VIEWPORT_MARGIN,
}) {
  const spaceBelow = viewport.height - anchor.bottom - margin;
  const spaceAbove = anchor.top - margin;
  const desiredHeight = Math.min(contentHeight, maxHeight);
  const placement = choosePopoverPlacement({ spaceAbove, spaceBelow, desiredHeight, gap });
  const available = (placement === "top" ? spaceAbove : spaceBelow) - gap;
  const height = Math.max(Math.min(maxHeight, available), Math.min(desiredHeight, MIN_POPOVER_HEIGHT));

  const width = Math.max(0, Math.min(contentWidth, viewport.width - margin * 2));
  const preferredLeft = align === "end" ? anchor.right - width : anchor.left;
  const left = Math.min(Math.max(preferredLeft, margin), viewport.width - margin - width);

  return {
    placement,
    top: placement === "bottom" ? Math.round(anchor.bottom + gap) : null,
    bottom: placement === "top" ? Math.round(viewport.height - anchor.top + gap) : null,
    left: Math.round(left),
    width: Math.round(width),
    maxHeight: Math.max(0, Math.floor(height)),
  };
}

function viewportSize() {
  const doc = typeof document !== "undefined" ? document.documentElement : null;
  return {
    width: doc?.clientWidth || window.innerWidth || 1024,
    height: window.innerHeight || doc?.clientHeight || 768,
  };
}

const samePosition = (a, b) =>
  a && b && ["placement", "top", "bottom", "left", "width", "maxHeight"].every((k) => a[k] === b[k]);

/** CSS custom properties consumed by ``.as-popover``. */
export function popoverStyle(pos) {
  if (!pos) return { visibility: "hidden" };
  return {
    "--as-pop-top": pos.top === null ? "auto" : `${pos.top}px`,
    "--as-pop-bottom": pos.bottom === null ? "auto" : `${pos.bottom}px`,
    "--as-pop-left": `${pos.left}px`,
    "--as-pop-width": `${pos.width}px`,
    "--as-pop-max-height": `${pos.maxHeight}px`,
  };
}

/**
 * Shared collision-aware positioning for every Auto Sourcing popover
 * (comboboxes, preset action menu). Placement is chosen when the popover
 * opens and re-evaluated on window resize and on any ancestor scroll
 * (rAF-throttled). ``measure(el)`` returns the panel's natural height,
 * independent of the max-height applied here, so updates never feed back
 * into placement.
 */
export function useFloatingPopover({
  open,
  anchorRef,
  floatingRef,
  measure,
  width = null,
  maxHeight = Infinity,
  align = "start",
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    let frame = 0;
    const update = () => {
      const anchor = anchorRef.current?.getBoundingClientRect?.();
      const el = floatingRef.current;
      if (!anchor || !el) return;
      const next = computePopoverPosition({
        anchor,
        viewport: viewportSize(),
        contentHeight: measure ? measure(el) : el.scrollHeight,
        contentWidth: width ?? el.offsetWidth,
        maxHeight,
        align,
      });
      setPosition((prev) => (samePosition(prev, next) ? prev : next));
    };
    const schedule = (event) => {
      if (event?.type === "scroll" && floatingRef.current?.contains?.(event.target)) return;
      if (frame) return;
      frame = -1;
      const id = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
      if (frame) frame = id;
    };
    update();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    return () => {
      if (frame > 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, width, maxHeight, align]);

  return position;
}
